import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { REELS } from '@/shared/data';
import { usePane } from '@/shell/Pane';
import { ReelItem } from './components/ReelItem';
import { ReelOverlay } from './components/ReelOverlay';
import { CommentsSheet } from './components/CommentsSheet';

export function Feed() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-reel-index]'));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number((entry.target as HTMLElement).dataset.reelIndex);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        }
      },
      { root, threshold: [0, 0.6, 1] },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Pause-on-hide defense layer 1: visibilitychange listener pauses the
  // (single, active-only) <video> when the document is hidden — covers
  // backgrounding the WebView host app.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        const root = containerRef.current;
        const v = root?.querySelector('video');
        v?.pause();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Pause-on-hide defense layer 2: when this pane goes inactive (user
  // switched to another mode), pause the active video. We read pane-active
  // state from PaneContext rather than useLocation/useTabHistory, because
  // PersistentPanes passes a frozen location to inactive panes' <Routes>
  // (so useLocation inside an inactive pane returns the frozen URL, which
  // would make this effect think we're still active).
  const { active: paneActive } = usePane();
  useEffect(() => {
    if (!paneActive) {
      const root = containerRef.current;
      const v = root?.querySelector('video');
      v?.pause();
    }
  }, [paneActive]);

  const activeReel = REELS[activeIndex] ?? null;

  return (
    <div
      ref={containerRef}
      data-testid="reelsflix-home"
      // Reelsflix is full-bleed: escape AppShell's pt-14 / pb-24 so each reel
      // genuinely fills the viewport. Bottom nav still floats over the bottom
      // (TikTok-style); ReelOverlay actions are positioned with bottom-24/28
      // to stay above the nav.
      className="h-[100dvh] -mt-14 -mb-24 overflow-y-auto snap-y snap-mandatory scrollbar-hide bg-black overscroll-contain"
    >
      {REELS.map((r, i) => (
        <section
          key={r.id}
          data-reel={r.id}
          data-reel-index={i}
          className="relative h-[100dvh] w-full snap-start snap-always"
        >
          <ReelItem reel={r} isActive={i === activeIndex}>
            <ReelOverlay reel={r} onOpenComments={() => setCommentsOpen(true)} />
          </ReelItem>
        </section>
      ))}
      {/* End-of-feed loader — a full-height snap target after the last reel,
          so scrolling past the bottom lands here cleanly. Indicates "more
          content is on the way" even though the static demo never resolves
          it. */}
      <section
        data-testid="reelsflix-end-loader"
        className="relative h-[100dvh] w-full snap-start snap-always bg-black flex flex-col items-center justify-center text-white/50"
      >
        <Loader2 className="w-9 h-9 animate-spin mb-4 text-white/60" />
        <div className="text-xs uppercase tracking-widest">Loading more reels…</div>
      </section>
      <CommentsSheet
        reelId={activeReel?.id ?? null}
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
      />
    </div>
  );
}
